// Shared request/response Zod schemas for the /api/v1/auth/* endpoints (WS-4).
// ADR-022 types layer: the `auth` Lambda re-exports these from its types/, and
// the WS-6 E2E suite imports them to assert response shape. All wire fields are
// snake_case per the ADR-002 envelope convention.

import { z } from 'zod';
import { apiEnvelope } from '../../envelope';
import { conversionAttributionSchema } from '../../attribution/schema';
import { clientSurfaceSchema } from '../../client-surface';
import { memberIdSchema } from '../../identity/legacy-identity-id';
import { migrationAuthenticateMigrationSchema } from '../migration';
import { SAML_SESSION_HANDLE_MAX_LENGTH, samlContactSchema } from '../../eligibility/saml';

/** OTP delivery channel — phone (SMS) or email. */
export const otpChannelSchema = z.enum(['sms', 'email']);
export type OtpChannel = z.infer<typeof otpChannelSchema>;

// ── POST /otp/initiate ────────────────────────────────────────────────────────
export const destinationOtpInitiateRequestSchema = z.object({
  channel: otpChannelSchema,
  /** E.164 phone (sms) or email address (email). */
  destination: z.string().min(1),
});
export type DestinationOtpInitiateRequest = z.infer<typeof destinationOtpInitiateRequestSchema>;

// The SAML form: the destination is the employer-roster contact
// sealed inside the session minted by POST /eligibility/saml/sessions. The
// client never holds the unmasked value, so it names the contact instead.
const samlOtpSourceShape = {
  saml_session: z.string().min(1).max(SAML_SESSION_HANDLE_MAX_LENGTH),
  contact: samlContactSchema,
};
export const samlOtpInitiateRequestSchema = z.object(samlOtpSourceShape).strict();
export type SamlOtpInitiateRequest = z.infer<typeof samlOtpInitiateRequestSchema>;

export const otpInitiateRequestSchema = z.union([
  destinationOtpInitiateRequestSchema,
  samlOtpInitiateRequestSchema,
]);
export type OtpInitiateRequest = z.infer<typeof otpInitiateRequestSchema>;

/**
 * A body naming both a typed destination and a sealed SAML contact. The typed
 * schema is the historical non-strict shape, so the union would parse such a
 * body as the typed form and silently drop the SAML keys; the route parsers
 * below refuse it instead, leaving the documented schemas unchanged.
 */
export function mixesOtpSources(raw: unknown): boolean {
  if (typeof raw !== 'object' || raw === null) return false;
  const typed = 'channel' in raw || 'destination' in raw;
  const saml = 'saml_session' in raw || 'contact' in raw;
  return typed && saml;
}

function refusingMixedSources<T>(schema: {
  safeParse: (raw: unknown) => { success: true; data: T } | { success: false };
}): { safeParse: (raw: unknown) => { success: true; data: T } | { success: false } } {
  return {
    safeParse: (raw) => (mixesOtpSources(raw) ? { success: false } : schema.safeParse(raw)),
  };
}

/** Narrow an OTP request to its SAML form. */
export function isSamlOtpRequest<T extends object>(
  request: T,
): request is Extract<T, { saml_session: string }> {
  return 'saml_session' in request;
}

// Response is intentionally identical for existing-member and new-signup
// destinations — no account enumeration (spec AC +).
export const otpInitiateDataSchema = z.object({
  otp_request_id: z.string(),
  expires_in_seconds: z.number().int().positive(),
  // How long until another code may be requested for this destination. The
  // server's min-interval is a per-environment dial, so a client that hardcodes
  // its resend cooldown re-enables the control early and the request is refused
  // — the client drives its countdown from this instead of guessing.
  //
  // Optional because a new client bundle can reach an API deployed before this
  // field existed, and that is the direction `.optional()` actually protects
  // (ADR-135 Rule 6). Clients fall back to their own constant when it is absent.
  resend_available_in_seconds: z.number().int().positive().optional(),
});
export type OtpInitiateData = z.infer<typeof otpInitiateDataSchema>;
export const otpInitiateResponseSchema = apiEnvelope(otpInitiateDataSchema);

// ── POST /otp/verify ──────────────────────────────────────────────────────────
const otpVerifyCommonShape = {
  code: z.string().regex(/^\d{6}$/),
  // The two sign-up consent controls. Both must be true on a first-ever signup
  // for this destination, else CONSENT_REQUIRED; returning members omit them
  // (they consented already). Optional here so the same request shape serves
  // both new and returning verify; the server enforces the new-user rule.
  // `terms_consent` is the policies control (Terms of Use, Privacy Policy,
  // Notice of Privacy Practices, calls and texts). `hipaa_consent` is the care
  // control: the Informed Consent for Telehealth Services and the 42 CFR Part 2
  // release (`telehealth_informed` + `part2_release`); its name is historical
  // and was deliberately kept so no client or column had to be renamed.
  terms_consent: z.boolean().optional(),
  hipaa_consent: z.boolean().optional(),
  // Sent `true` only by a client whose care control shows and links the
  // Part 2 Consent for Disclosure of Data. Without it the server treats the sign-up
  // as one made on the earlier copy, which never showed that document, and
  // writes no `part2_release` row. Optional so a client bundle older than this
  // field keeps working (a store binary's first launch runs its embedded
  // bundle), and so its absence is the safe reading.
  part2_consent: z.boolean().optional(),
  // Sent `true` only by a client whose policies control states the agreement
  // "to receive calls and text messages from Pelago". A first-ever sign-up that
  // sends it records a `calls_and_texts` consent; without it none is recorded,
  // because older clients showed the same control without that clause.
  // Optional so a client bundle older than this field keeps working, and so its
  // absence is the safe reading.
  calls_and_texts_consent: z.boolean().optional(),
  // Sent `true` only by a client whose policies control shows and links the
  // TCPA Authorization. Without it the server treats the sign-up as one made on
  // the earlier copy, whose policies control named three documents and never
  // that one, and writes no `tcpa_authorization` row. Optional so a client
  // bundle older than this field keeps working, and so its absence is the safe
  // reading.
  tcpa_authorization_consent: z.boolean().optional(),
  // The device's live landing capture (ADR-132). A verify that creates the
  // account produces member.signed_up, a designated conversion, so the record
  // rides here; on a returning member's sign-in it is ignored. Validated so a
  // malformed record is a 400, never a silently dropped dimension.
  attribution: conversionAttributionSchema.optional(),
  // The client says which surface it is. When present it beats the server's
  // fallback guess from the Origin header, which a proxy can strip. Optional
  // because every installed native build predates it and declares nothing.
  surface: clientSurfaceSchema.optional(),
};

export const destinationOtpVerifyRequestSchema = z.object({
  channel: otpChannelSchema,
  destination: z.string().min(1),
  ...otpVerifyCommonShape,
});
export type DestinationOtpVerifyRequest = z.infer<typeof destinationOtpVerifyRequestSchema>;

/** The SAML form of verify: the same sealed contact the code was sent to. */
export const samlOtpVerifyRequestSchema = z
  .object({ ...samlOtpSourceShape, ...otpVerifyCommonShape })
  .strict();
export type SamlOtpVerifyRequest = z.infer<typeof samlOtpVerifyRequestSchema>;

export const otpVerifyRequestSchema = z.union([
  destinationOtpVerifyRequestSchema,
  samlOtpVerifyRequestSchema,
]);
export type OtpVerifyRequest = z.infer<typeof otpVerifyRequestSchema>;

/** Route-side parsers for the two OTP bodies: the schemas, plus the mixed-source refusal. */
export const otpInitiateRequestParser = refusingMixedSources(otpInitiateRequestSchema);
export const otpVerifyRequestParser = refusingMixedSources(otpVerifyRequestSchema);

export const tokenBundleDataSchema = z.object({
  access_token: z.string(),
  id_token: z.string(),
  refresh_token: z.string(),
  expires_in: z.number().int().positive(),
  member_id: memberIdSchema,
});
export type TokenBundleData = z.infer<typeof tokenBundleDataSchema>;

// The verify response is the token bundle plus, for a Pelago 1.0 member whose
// migration is not done, the same migration signal /migration/authenticate
// returns. The nested object is that contract's own type (camelCase), owned in
// one place. Present only when a migration start is required; a non-legacy or
// already-migrated member's response carries no `migration` key. Deliberately
// not `.strict()` (ADR-135): a client-parsed response must tolerate new keys.
export const otpVerifyDataSchema = tokenBundleDataSchema.extend({
  migration: migrationAuthenticateMigrationSchema.optional(),
});
export type OtpVerifyData = z.infer<typeof otpVerifyDataSchema>;
export const otpVerifyResponseSchema = apiEnvelope(otpVerifyDataSchema);

// ── POST /preview-token ──────────────────────────────────────────────────────
// Dev/preview-only testing bridge. Returns the same token bundle shape as OTP
// verify so clients keep a single "store access_token" contract.
//
// The body is optional and historically absent. Automation may opt into a
// per-run isolated member by sending `run_key`: the server derives a
// run-classed destination from it instead of the stable per-env alias, so
// state-sensitive runs stop racing every other consumer of the shared member.
// Omitting the body (or sending `{}`) keeps the stable-member behaviour.
export const previewTokenRequestSchema = z.object({
  /** Lowercase run identifier; same key ⇒ same member within its lifetime. */
  run_key: z
    .string()
    .regex(/^[a-z0-9][a-z0-9-]{0,39}$/)
    .optional(),
});
export type PreviewTokenRequest = z.infer<typeof previewTokenRequestSchema>;

export type PreviewTokenData = TokenBundleData;
export const previewTokenResponseSchema = apiEnvelope(tokenBundleDataSchema);

// ── POST /migration-demo-member ───────────────────────────────────────────────
// Optional body selects which onboarding answers the migration demo seed writes
// before the intake session is finalised — the nightly migration-gap collection
// compositions use `gaps-all`, `gaps-program-only`, and `gaps-none` fixtures.
export const migrationDemoGapPresetSchema = z.enum(['gaps-all', 'gaps-program-only', 'gaps-none']);
export type MigrationDemoGapPreset = z.infer<typeof migrationDemoGapPresetSchema>;

export const migrationDemoMemberRequestSchema = z.object({
  preset: migrationDemoGapPresetSchema.optional(),
});
export type MigrationDemoMemberRequest = z.infer<typeof migrationDemoMemberRequestSchema>;

// ── POST /refresh ─────────────────────────────────────────────────────────────
export const refreshRequestSchema = z.object({
  refresh_token: z.string().min(1),
});
export type RefreshRequest = z.infer<typeof refreshRequestSchema>;

export const refreshDataSchema = z.object({
  access_token: z.string(),
  id_token: z.string(),
  refresh_token: z.string(),
  expires_in: z.number().int().positive(),
});
export type RefreshData = z.infer<typeof refreshDataSchema>;
export const refreshResponseSchema = apiEnvelope(refreshDataSchema);

// ── POST /sign-out ────────────────────────────────────────────────────────────
// Authenticated via bearer token (no body); revokes all refresh tokens.
export const signOutDataSchema = z.object({ signed_out: z.literal(true) });
export type SignOutData = z.infer<typeof signOutDataSchema>;
export const signOutResponseSchema = apiEnvelope(signOutDataSchema);

// ── GET /me ───────────────────────────────────────────────────────────────────
// Authenticated via bearer token; returns the resolved member profile.
export const meDataSchema = z.object({
  member_id: memberIdSchema,
  email: z.string().nullable(),
  phone_number: z.string().nullable(),
  locale: z.string().nullable(),
  status: z.string(),
});
export type MeData = z.infer<typeof meDataSchema>;
export const meResponseSchema = apiEnvelope(meDataSchema);

// ── POST /session-activity ────────────────────────────────────────────────────
// Authenticated via bearer token. The mobile client reports the first
// meaningful interaction of an app session (the session boundary is the
// client's, per the ADR-090 coordinator); the server records it as a
// `session_active` mechanism and publishes the member.session.started outcome.
// `app_session_id` is a client-minted opaque UUID — never logged, never lifted.
export const sessionActivityInteractionSchema = z.enum(['screen_view', 'action']);
export type SessionActivityInteraction = z.infer<typeof sessionActivityInteractionSchema>;
export const sessionActivityStartReasonSchema = z.enum(['launch', 'resume']);
export type SessionActivityStartReason = z.infer<typeof sessionActivityStartReasonSchema>;
export const sessionActivityPlatformSchema = z.enum(['ios', 'android']);
export type SessionActivityPlatform = z.infer<typeof sessionActivityPlatformSchema>;
export const SESSION_ACTIVITY_APP_VERSION_MAX_LENGTH = 40;
export const sessionActivityRequestSchema = z.object({
  app_session_id: z.string().uuid(),
  interaction: sessionActivityInteractionSchema,
  start_reason: sessionActivityStartReasonSchema,
  platform: sessionActivityPlatformSchema,
  app_version: z.string().min(1).max(SESSION_ACTIVITY_APP_VERSION_MAX_LENGTH),
});
export type SessionActivityRequest = z.infer<typeof sessionActivityRequestSchema>;

export const sessionActivityDataSchema = z.object({ app_session_id: z.string().uuid() });
export type SessionActivityData = z.infer<typeof sessionActivityDataSchema>;
export const sessionActivityResponseSchema = apiEnvelope(sessionActivityDataSchema);
